import React, { useState, useEffect, useCallback } from 'react';
import {
  Search, Filter, Eye, CheckCircle, XCircle, Clock, AlertTriangle,
  Download, RefreshCw, User, MapPin, DollarSign, Upload, AlertCircle,
  Check, Shield, ChevronDown, ChevronUp, Circle, ArrowRight, Layers,
  FileText, TrendingUp, Award, Hourglass, Trash2, BarChart2, PieChart,
  Activity, Users, AlertOctagon
} from 'lucide-react';
import WillingnessService from './willingness/WillingnessService';

// ── Palette ──────────────────────────────────────────────────────────────────
const P = {
  navy:    '#001f3f',
  navyMid: '#00294f',
  teal:    '#0d9488',
  tealL:   '#14b8a6',
  amber:   '#f59e0b',
  rose:    '#f43f5e',
  emerald: '#10b981',
  blue:    '#3b82f6',
  violet:  '#8b5cf6',
  s50:     '#f8fafc',
  s100:    '#f1f5f9',
  s200:    '#e2e8f0',
  s400:    '#94a3b8',
  s500:    '#64748b',
  s700:    '#334155',
  s800:    '#1e293b',
};

// ── Status/chain config ───────────────────────────────────────────────────────
const CHAIN_STATUS_CFG = {
  not_started:         { label: 'Haijanza',            color: P.s400,    bg: '#f1f5f9' },
  in_progress:         { label: 'Inaendelea',          color: P.amber,   bg: '#fef3c7' },
  pending_admin:       { label: 'Mkurugenzi Akague',   color: P.blue,    bg: '#dbeafe' },
  admin_approved:      { label: 'Imekubaliwa',         color: P.emerald, bg: '#d1fae5' },
  admin_rejected:      { label: 'Imekataliwa',         color: P.rose,    bg: '#ffe4e6' },
  rejected_by_officer: { label: 'Mkaguzi Alikataa',   color: P.rose,    bg: '#ffe4e6' },
};

const STATUS_CFG = {
  pending:      { label: 'Pending',      color: P.amber,   bg: '#fef3c7' },
  under_review: { label: 'Under Review', color: P.blue,    bg: '#dbeafe' },
  approved:     { label: 'Approved',     color: P.emerald, bg: '#d1fae5' },
  rejected:     { label: 'Rejected',     color: P.rose,    bg: '#ffe4e6' },
  completed:    { label: 'Completed',    color: P.teal,    bg: '#ccfbf1' },
};

const ROLE_CFG = {
  chairperson: { label: 'Mwenyekiti', color: P.amber,   icon: '👑' },
  secretary:   { label: 'Katibu',     color: P.blue,    icon: '📖' },
  treasurer:   { label: 'Mhazini',   color: P.emerald, icon: '💰' },
  admin:       { label: 'Msimamizi', color: P.rose,    icon: '🛡️' },
  member:      { label: 'Mwanachama',color: P.s500,    icon: '👤' },
};

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmt = (n) => new Intl.NumberFormat('en-TZ').format(parseFloat(n) || 0);
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('sw-TZ', { day:'2-digit', month:'short', year:'numeric' }) : '—';
// camelCase form_data keys (e.g. beneficiaryName, hospitalLocation) -> "Beneficiary Name"
const humanizeKey = (key) => key.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/^./, (c) => c.toUpperCase());
// form_data values can be plain, or nested objects (hospitalLocation, funeralHome, deceasedLocation) -
// flatten those into "field: value" pairs instead of showing "[object Object]".
const formatFormDataValue = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Ndiyo' : 'Hapana';
  if (typeof value === 'object') {
    return Object.entries(value)
      .filter(([, v]) => v !== null && v !== undefined && v !== '')
      .map(([k, v]) => `${humanizeKey(k)}: ${v}`)
      .join(', ') || '—';
  }
  return String(value);
};
const Chip = ({ label, color, bg }) => (
  <span style={{ color, backgroundColor: bg, fontSize: 11, fontWeight: 700, borderRadius: 20,
    padding: '2px 10px', display:'inline-block', whiteSpace:'nowrap' }}>
    {label}
  </span>
);

// ── Approval Chain Visualizer ─────────────────────────────────────────────────
const ChainView = ({ steps = [], currentStep, chainStatus }) => {
  if (!steps.length) return (
    <div style={{ fontSize: 13, padding: '12px 0' }}>
      {chainStatus === 'in_progress' ? (
        <div style={{ backgroundColor: '#fef3c7', borderRadius: 8, padding: '10px 14px', color: '#92400e' }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>Ombi lina hali in_progress lakini halina kikundi.</div>
          <div style={{ fontSize: 12 }}>Mnyororo una hatua ya Msimamizi tu. Tumia Override hapa chini.</div>
        </div>
      ) : (
        <div style={{ color: P.s400, textAlign: 'center' }}>Mnyororo wa idhini haujaanzishwa bado.</div>
      )}
    </div>
  );

  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: P.s500, textTransform: 'uppercase',
        letterSpacing: '.6px', marginBottom: 10 }}>Mnyororo wa Idhini</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {steps.map((step, i) => {
          const isCurrent = step.step_number === currentStep && !step.decision;
          // gang_role is object {id,role_type,name} (new serializer) or null/int (old)
          const rt = (step.gang_role && typeof step.gang_role === 'object' && step.gang_role.role_type)
            ? step.gang_role.role_type
            : step.approver_type === 'admin' ? 'admin' : 'member';
          const rc = ROLE_CFG[rt] || ROLE_CFG.member;
          const approved = step.decision === 'approved';
          const rejected = step.decision === 'rejected';
          const skipped  = step.decision === 'skipped';

          const circleBg = approved ? P.emerald : rejected ? P.rose : isCurrent ? P.amber : P.s200;
          const rowBg    = approved ? '#f0fdf4' : rejected ? '#fff1f2' : isCurrent ? '#fffbeb' : '#fff';
          const border   = approved ? '#bbf7d0' : rejected ? '#fecdd3' : isCurrent ? '#fde68a' : P.s200;

          return (
            <div key={step.id || i} style={{
              display: 'flex', alignItems: 'center', gap: 10,
              backgroundColor: rowBg, border: `1.5px solid ${border}`,
              borderRadius: 10, padding: '8px 12px',
            }}>
              {/* Circle */}
              <div style={{ width: 30, height: 30, borderRadius: 15, flexShrink: 0,
                backgroundColor: circleBg, display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 13, color: '#fff', fontWeight: 700 }}>
                {approved ? '✓' : rejected ? '✕' : skipped ? '↷' : step.step_number}
              </div>

              {/* Info */}
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: rc.color,
                    backgroundColor: rc.color + '18', borderRadius: 6, padding: '2px 8px' }}>
                    {rc.icon} {step.gang_role?.name || (step.approver_type === 'admin' ? 'Msimamizi Mkuu' : rc.label)}
                  </span>
                  {isCurrent && (
                    <span style={{ fontSize: 10, color: P.amber, fontWeight: 700,
                      backgroundColor: '#fef3c7', borderRadius: 6, padding: '2px 7px' }}>
                      ⏳ Inasubiri
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 12, fontWeight: 600, color: P.s800 }}>
                  {step.approver_name || 'Hakuna aliyepewa jukumu hili'}
                </div>
                {step.decided_at && (
                  <div style={{ fontSize: 11, color: P.s400 }}>{fmtDate(step.decided_at)}</div>
                )}
                {step.notes && (
                  <div style={{ fontSize: 11, color: P.s500, fontStyle: 'italic', marginTop: 2 }}>
                    "{step.notes}"
                  </div>
                )}
              </div>

              {/* Decision badge */}
              {step.decision && (
                <div style={{
                  fontSize: 10, fontWeight: 700, borderRadius: 8, padding: '3px 9px',
                  backgroundColor: approved ? '#d1fae5' : rejected ? '#ffe4e6' : P.s100,
                  color: approved ? P.emerald : rejected ? P.rose : P.s400,
                }}>
                  {approved ? 'Amekubali' : rejected ? 'Amekataa' : 'Alipita'}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ── Stat Card ─────────────────────────────────────────────────────────────────
const StatCard = ({ label, value, icon, color, bg }) => (
  <div style={{ backgroundColor: '#fff', borderRadius: 14, padding: '18px 20px',
    border: `1px solid ${P.s200}`, display: 'flex', alignItems: 'center', gap: 14 }}>
    <div style={{ width: 48, height: 48, borderRadius: 12, backgroundColor: bg,
      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <span style={{ fontSize: 22 }}>{icon}</span>
    </div>
    <div>
      <div style={{ fontSize: 26, fontWeight: 800, color }}>{value}</div>
      <div style={{ fontSize: 12, color: P.s500, fontWeight: 600 }}>{label}</div>
    </div>
  </div>
);

// ── Admin Final Approve Panel (inline) ───────────────────────────────────────
const AdminApprovePanel = ({ request, onApprove, loading }) => {
  const [decision, setDecision]     = useState('');
  const [notes, setNotes]           = useState('');
  const [amount, setAmount]         = useState(request.requested_amount || '');
  const [err, setErr]               = useState('');

  const submit = () => {
    setErr('');
    if (!decision) { setErr('Chagua uamuzi'); return; }
    if (decision === 'approved' && (!amount || parseFloat(amount) <= 0)) {
      setErr('Ingiza kiasi kilichoidhinishwa'); return;
    }
    if (decision === 'rejected' && !notes.trim()) {
      setErr('Toa sababu ya kukataa'); return;
    }
    onApprove({ decision, notes, approved_amount: parseFloat(amount) });
  };

  return (
    <div style={{ backgroundColor: P.navy, borderRadius: 14, padding: 20, marginTop: 14 }}>
      <div style={{ color: '#fff', fontWeight: 700, fontSize: 14, marginBottom: 14 }}>
        🛡️ Idhini Yako ya Mwisho — Msimamizi
      </div>

      {err && (
        <div style={{ color: P.rose, backgroundColor: '#ffe4e6', borderRadius: 8,
          padding: '8px 12px', fontSize: 13, marginBottom: 12 }}>{err}</div>
      )}

      <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
        {['approved', 'rejected'].map(d => (
          <button key={d} onClick={() => setDecision(d)}
            style={{
              flex: 1, padding: '10px 0', borderRadius: 10, border: 'none', cursor: 'pointer',
              fontWeight: 700, fontSize: 13,
              backgroundColor: decision === d
                ? (d === 'approved' ? P.emerald : P.rose)
                : 'rgba(255,255,255,0.1)',
              color: decision === d ? '#fff' : 'rgba(255,255,255,0.6)',
              transition: 'all .15s',
            }}>
            {d === 'approved' ? '✓ Kubali' : '✕ Kataa'}
          </button>
        ))}
      </div>

      {decision === 'approved' && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ color: 'rgba(255,255,255,0.65)', fontSize: 12, marginBottom: 5 }}>
            Kiasi Kilichoidhinishwa (TZS) *
          </div>
          <input
            type="number" value={amount}
            onChange={e => setAmount(e.target.value)}
            max={request.requested_amount}
            style={{ width: '100%', padding: '9px 12px', borderRadius: 8,
              border: '1px solid rgba(255,255,255,0.2)', backgroundColor: 'rgba(255,255,255,0.08)',
              color: '#fff', fontSize: 14, boxSizing: 'border-box' }}
          />
          <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, marginTop: 3 }}>
            Kiwango kilichoombwa: TZS {fmt(request.requested_amount)}
          </div>
        </div>
      )}

      <div style={{ marginBottom: 14 }}>
        <div style={{ color: 'rgba(255,255,255,0.65)', fontSize: 12, marginBottom: 5 }}>
          Maelezo {decision === 'rejected' ? '*' : '(hiari)'}
        </div>
        <textarea
          value={notes} onChange={e => setNotes(e.target.value)}
          rows={2}
          placeholder={decision === 'rejected' ? 'Toa sababu ya kukataa...' : 'Maelezo ya ziada...'}
          style={{ width: '100%', padding: '9px 12px', borderRadius: 8, resize: 'none',
            border: '1px solid rgba(255,255,255,0.2)', backgroundColor: 'rgba(255,255,255,0.08)',
            color: '#fff', fontSize: 13, boxSizing: 'border-box' }}
        />
      </div>

      <button onClick={submit} disabled={loading || !decision}
        style={{ width: '100%', padding: '11px 0', borderRadius: 10, border: 'none',
          cursor: loading || !decision ? 'not-allowed' : 'pointer', fontWeight: 700, fontSize: 14,
          backgroundColor: decision === 'rejected' ? P.rose : P.teal,
          color: '#fff', opacity: loading || !decision ? .6 : 1,
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
        {loading ? '⏳ Inashughulika...' : decision === 'approved' ? '✓ Toa Idhini' : '✕ Kataa Ombi'}
      </button>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ══════════════════════════════════════════════════════════════════════════════
const ServiceRequestManager = () => {
  const [requests, setRequests]           = useState([]);
  const [filtered, setFiltered]           = useState([]);
  const [chains, setChains]               = useState({});     // { [id]: steps[] }
  const [expanded, setExpanded]           = useState(null);
  const [loading, setLoading]             = useState(true);
  const [refreshing, setRefreshing]       = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [stats, setStats]                 = useState(null);
  const [notification, setNotification]   = useState(null);
  const [filters, setFilters]             = useState({
    search: '', service_type: '', status: '', chain_status: '', priority: ''
  });
  const [showFilters, setShowFilters]     = useState(false);
  const [selectedIds, setSelectedIds]     = useState([]);
  const [deleteModal, setDeleteModal]     = useState(null);   // { id, title }
  const [showReport, setShowReport]       = useState(false);
  const [deleting, setDeleting]           = useState(false);

  // ── Notification ────────────────────────────────────────────────────────────
  const notify = (msg, type = 'success') => {
    setNotification({ msg, type });
    setTimeout(() => setNotification(null), 4500);
  };

  // ── Fetch all requests ───────────────────────────────────────────────────────
  const loadRequests = useCallback(async (isRefresh = false) => {
    isRefresh ? setRefreshing(true) : setLoading(true);
    try {
      const data = await WillingnessService.fetchServiceRequests({});
      const arr  = data.requests || data.results || [];
      setRequests(arr);

      // fetch stats
      try {
        const s = await WillingnessService.fetchServiceRequestStatistics();
        setStats(s);
      } catch { /* silent */ }

      // pre-fetch chains for in_progress / pending_admin
      const needsChain = arr.filter(r =>
        ['in_progress', 'pending_admin'].includes(r.approval_chain_status)
      );
      const results = await Promise.all(needsChain.map(async r => {
        try {
          // Try approval-chain endpoint first (returns full chain with role info)
          let steps = [];
          if (WillingnessService.fetchApprovalChain) {
            const d = await WillingnessService.fetchApprovalChain(r.id);
            steps = d.approval_chain || d.results || d.steps || [];
          }
          // Fallback: service-approvals list endpoint
          if (!steps.length && WillingnessService.fetchApprovalSteps) {
            const d2 = await WillingnessService.fetchApprovalSteps(r.id);
            steps = d2.results || d2.approval_steps || [];
          }
          return { id: r.id, steps };
        } catch { return { id: r.id, steps: [] }; }
      }));
      const map = {};
      results.forEach(({ id, steps }) => { map[id] = steps; });
      setChains(map);

    } catch (e) {
      notify('Imeshindwa kupakia maombi: ' + e.message, 'error');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { loadRequests(); }, [loadRequests]);

  // ── Filtering ────────────────────────────────────────────────────────────────
  useEffect(() => {
    let r = [...requests];
    const q = filters.search.toLowerCase();
    if (q) r = r.filter(x =>
      x.title?.toLowerCase().includes(q) ||
      x.reference_number?.toLowerCase().includes(q) ||
      x.user?.name?.toLowerCase().includes(q)
    );
    if (filters.service_type) r = r.filter(x => x.service_type === filters.service_type);
    if (filters.status)       r = r.filter(x => x.status === filters.status);
    if (filters.chain_status) r = r.filter(x => x.approval_chain_status === filters.chain_status);
    if (filters.priority)     r = r.filter(x => x.priority === filters.priority);
    setFiltered(r);
  }, [filters, requests]);

  // ── Expand + lazy fetch chain ─────────────────────────────────────────────────
  const toggleExpand = async (id) => {
    if (expanded === id) { setExpanded(null); return; }
    setExpanded(id);
    if (!chains[id]) {
      try {
        let steps = [];
        if (WillingnessService.fetchApprovalChain) {
          const d = await WillingnessService.fetchApprovalChain(id);
          steps = d.approval_chain || d.results || d.steps || [];
        }
        if (!steps.length && WillingnessService.fetchApprovalSteps) {
          const d2 = await WillingnessService.fetchApprovalSteps(id);
          steps = d2.results || d2.approval_steps || [];
        }
        setChains(prev => ({ ...prev, [id]: steps }));
      } catch { /* silent */ }
    }
  };

  // ── Admin final approve ───────────────────────────────────────────────────────
  const handleAdminApprove = async (requestId, payload) => {
    setActionLoading(true);
    try {
      await WillingnessService.adminFinalApprove(requestId, payload);
      notify(payload.decision === 'approved' ? 'Ombi limeidhinishwa!' : 'Ombi limekataliwa!');
      await loadRequests(true);
      setExpanded(null);
    } catch (e) {
      notify('Imeshindwa: ' + e.message, 'error');
    } finally { setActionLoading(false); }
  };

  // ── Officer step approve (admin acting as officer via update-status) ───────────
  const handleQuickStatus = async (requestId, statusData) => {
    setActionLoading(true);
    try {
      await WillingnessService.updateServiceRequestStatus(requestId, statusData);
      notify('Hali imesasishwa!');
      await loadRequests(true);
      setExpanded(null);
    } catch (e) {
      notify('Imeshindwa: ' + e.message, 'error');
    } finally { setActionLoading(false); }
  };

  // ── Delete request ───────────────────────────────────────────────────────────
  const handleDelete = async () => {
    if (!deleteModal) return;
    setDeleting(true);
    try {
      await WillingnessService.deleteServiceRequest(deleteModal.id);
      notify('Ombi limefutwa!');
      setDeleteModal(null);
      await loadRequests(true);
    } catch (e) {
      notify('Imeshindwa kufuta: ' + e.message, 'error');
    } finally { setDeleting(false); }
  };

  // ── Export ────────────────────────────────────────────────────────────────────
  const doExport = async () => {
    try {
      const blob = await WillingnessService.exportServiceRequests({}, 'csv');
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href = url; a.download = `requests_${Date.now()}.csv`;
      a.click(); URL.revokeObjectURL(url);
      notify('Export imekamilika!');
    } catch { notify('Export imeshindwa', 'error'); }
  };

  // ── Derived ───────────────────────────────────────────────────────────────────
  // Include both formally pending_admin AND gang=null requests (admin-only chain, 1 step)
  const pendingAdmin = requests.filter(r => {
    if (r.approval_chain_status === 'pending_admin') return true;
    const ch = chains[r.id] || [];
    return ch.length === 1 && ch[0]?.approver_type === 'admin' && ch[0]?.decision === 'pending';
  });
  const inProgress   = requests.filter(r => r.approval_chain_status === 'in_progress');

  // ── Computed report data ──────────────────────────────────────────────────────
  const reportData = {
    byStatus:      Object.entries(STATUS_CFG).map(([k, v]) => ({
      label: v.label, count: requests.filter(r => r.status === k).length, color: v.color, bg: v.bg
    })),
    byChain:       Object.entries(CHAIN_STATUS_CFG).map(([k, v]) => ({
      label: v.label, count: requests.filter(r => r.approval_chain_status === k).length, color: v.color, bg: v.bg
    })),
    byServiceType: [...new Set(requests.map(r => r.service_type))].map(t => ({
      label: t, count: requests.filter(r => r.service_type === t).length
    })),
    totalAmount:   requests.reduce((s, r) => s + parseFloat(r.requested_amount || 0), 0),
    approvedAmount:requests.filter(r => r.status === 'approved').reduce((s, r) => s + parseFloat(r.approved_amount || 0), 0),
    avgDays:       requests.length
      ? Math.round(requests.reduce((s, r) => s + (r.days_since_request || 0), 0) / requests.length)
      : 0,
    uniqueUsers:   new Set(requests.map(r => r.user?.id)).size,
  };

  // ══════════════════════════════════════════════════════════════════════════════
  // RENDER
  // ══════════════════════════════════════════════════════════════════════════════
  return (
    <div style={{ minHeight: '100vh', backgroundColor: P.s50, fontFamily: "'DM Sans', system-ui, sans-serif" }}>

      {/* ── Notification ── */}
      {notification && (
        <div style={{
          position: 'fixed', top: 20, right: 20, zIndex: 9999,
          backgroundColor: notification.type === 'success' ? P.emerald : P.rose,
          color: '#fff', borderRadius: 12, padding: '12px 18px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
          display: 'flex', alignItems: 'center', gap: 10, maxWidth: 380,
          animation: 'slideIn .25s ease',
        }}>
          <span style={{ fontSize: 16 }}>{notification.type === 'success' ? '✓' : '!'}</span>
          <span style={{ fontSize: 13, fontWeight: 600 }}>{notification.msg}</span>
          <button onClick={() => setNotification(null)}
            style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 16 }}>
            ×
          </button>
        </div>
      )}

      {/* ── Header ── */}
      <div style={{
        background: `linear-gradient(135deg, ${P.navy} 0%, ${P.navyMid} 50%, #002d5a 100%)`,
        padding: '28px 32px 32px', position: 'relative', overflow: 'hidden',
      }}>
        {/* decorative blobs */}
        <div style={{ position:'absolute', top:-60, right:-60, width:200, height:200, borderRadius:100,
          backgroundColor:'rgba(13,148,136,0.15)', pointerEvents:'none' }} />
        <div style={{ position:'absolute', bottom:-30, left:-30, width:160, height:160, borderRadius:80,
          backgroundColor:'rgba(0,41,79,0.6)', pointerEvents:'none' }} />

        <div style={{ position:'relative', zIndex:1 }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:12 }}>
            <div>
              <div style={{ color:'rgba(255,255,255,0.6)', fontSize:12, fontWeight:700,
                textTransform:'uppercase', letterSpacing:'.8px', marginBottom:4 }}>
                JamboRicG Admin
              </div>
              <h1 style={{ color:'#fff', fontSize:26, fontWeight:800, margin:0, letterSpacing:'-.3px' }}>
                Usimamizi wa Maombi
              </h1>
              <p style={{ color:'rgba(255,255,255,0.55)', fontSize:13, margin:'4px 0 0' }}>
                Kagua, idhinisha, na fuatilia mnyororo wa idhini wa maombi yote
              </p>
            </div>

            <div style={{ display:'flex', gap:10 }}>
              <button onClick={doExport}
                style={{ padding:'9px 18px', borderRadius:10, border:'1px solid rgba(255,255,255,0.2)',
                  backgroundColor:'rgba(255,255,255,0.08)', color:'#fff', cursor:'pointer',
                  fontWeight:600, fontSize:13, display:'flex', alignItems:'center', gap:6 }}>
                <Download size={15} /> Export
              </button>
              <button onClick={() => setShowReport(r => !r)}
                style={{ padding:'9px 18px', borderRadius:10, border:'1px solid rgba(255,255,255,0.2)',
                  backgroundColor: showReport ? P.teal : 'rgba(255,255,255,0.08)',
                  color:'#fff', cursor:'pointer', fontWeight:600, fontSize:13,
                  display:'flex', alignItems:'center', gap:6 }}>
                <BarChart2 size={15} /> Ripoti
              </button>
              <button onClick={() => loadRequests(true)} disabled={refreshing}
                style={{ padding:'9px 18px', borderRadius:10, border:'none',
                  backgroundColor: P.teal, color:'#fff', cursor:'pointer',
                  fontWeight:700, fontSize:13, display:'flex', alignItems:'center', gap:6,
                  opacity: refreshing ? .7 : 1 }}>
                <RefreshCw size={15} style={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }} />
                {refreshing ? 'Inapakia...' : 'Sasisha'}
              </button>
            </div>
          </div>

          {/* ── Admin attention banner ── */}
          {pendingAdmin.length > 0 && (
            <div style={{
              marginTop: 20, backgroundColor: 'rgba(245,158,11,0.15)',
              border: '1px solid rgba(245,158,11,0.35)', borderRadius: 12,
              padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 10,
            }}>
              <div style={{ width:8, height:8, borderRadius:4, backgroundColor: P.amber,
                animation: 'pulse 1.5s infinite' }} />
              <span style={{ color:'#fcd34d', fontWeight:700, fontSize:13 }}>
                {pendingAdmin.length} ombi{pendingAdmin.length > 1 ? ' yanayosubiri' : ' linalosubiri'} idhini yako ya mwisho
              </span>
              <span style={{ marginLeft:'auto', color:'rgba(252,211,77,0.6)', fontSize:12 }}>
                Bonyeza kwenye ombi hapa chini
              </span>
            </div>
          )}
        </div>
      </div>

      <div style={{ padding: '24px 32px', maxWidth: 1300, margin: '0 auto' }}>

        {/* ── Stat Cards ── */}
        {stats && (
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(180px,1fr))', gap:12, marginBottom:24 }}>
            <StatCard label="Maombi Yote"   value={stats.total_requests || requests.length}  icon="📋" color={P.navy}    bg="#dbeafe" />
            <StatCard label="Yanayosubiri"  value={stats.summary?.pending || 0}              icon="⏳" color={P.amber}   bg="#fef3c7" />
            <StatCard label="Mkurugenzi Akague" value={pendingAdmin.length}                  icon="🛡️" color={P.rose}    bg="#ffe4e6" />
            <StatCard label="Zinazoendelea" value={inProgress.length}                        icon="🔄" color={P.blue}    bg="#dbeafe" />
            <StatCard label="Zilizokubaliwa" value={stats.summary?.approved || 0}            icon="✅" color={P.emerald} bg="#d1fae5" />
            <StatCard label="Zilizokataliwa" value={stats.summary?.rejected || 0}            icon="❌" color={P.rose}    bg="#ffe4e6" />
          </div>
        )}

        {/* ── Filters ── */}
        <div style={{ backgroundColor:'#fff', borderRadius:14, border:`1px solid ${P.s200}`,
          marginBottom:20, overflow:'hidden' }}>
          <div onClick={() => setShowFilters(f => !f)}
            style={{ padding:'14px 20px', display:'flex', alignItems:'center', gap:10,
              cursor:'pointer', userSelect:'none' }}>
            <Filter size={16} color={P.navy} />
            <span style={{ fontWeight:700, fontSize:14, color:P.s800 }}>Vichujio</span>
            <div style={{ marginLeft:'auto', display:'flex', alignItems:'center', gap:8 }}>
              {Object.values(filters).some(Boolean) && (
                <span style={{ backgroundColor:P.teal, color:'#fff', borderRadius:20,
                  fontSize:11, fontWeight:700, padding:'2px 9px' }}>Vichujio vimewekwa</span>
              )}
              {showFilters ? <ChevronUp size={16} color={P.s400} /> : <ChevronDown size={16} color={P.s400} />}
            </div>
          </div>

          {showFilters && (
            <div style={{ padding:'0 20px 18px', borderTop:`1px solid ${P.s100}` }}>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(160px,1fr))', gap:12, marginTop:14 }}>
                {/* Search */}
                <div style={{ position:'relative', gridColumn:'1 / -1' }}>
                  <Search size={14} style={{ position:'absolute', left:12, top:'50%', transform:'translateY(-50%)', color:P.s400 }} />
                  <input placeholder="Tafuta jina, ref, mtumiaji..."
                    value={filters.search} onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
                    style={{ width:'100%', padding:'9px 12px 9px 36px', border:`1px solid ${P.s200}`,
                      borderRadius:8, fontSize:13, boxSizing:'border-box', outline:'none' }} />
                </div>

                {[
                  { key:'service_type', label:'Aina ya Huduma', opts:[
                    ['matibabu','Matibabu'], ['msaada_kwa_wazee','Msaada Wazee'],
                    ['rambirambi','Rambirambi'], ['nauri_ya_uombolezaji','Nauri'],
                    ['kesi_za_umiliki','Kesi za Umiliki'],
                  ]},
                  { key:'status', label:'Hali ya Ombi', opts:Object.entries(STATUS_CFG).map(([k,v]) => [k,v.label]) },
                  { key:'chain_status', label:'Hali ya Mnyororo', opts:Object.entries(CHAIN_STATUS_CFG).map(([k,v]) => [k,v.label]) },
                  { key:'priority', label:'Kipaumbele', opts:[['low','Chini'],['medium','Kati'],['high','Juu'],['urgent','Haraka']] },
                ].map(({ key, label, opts }) => (
                  <select key={key} value={filters[key]}
                    onChange={e => setFilters(f => ({ ...f, [key]: e.target.value }))}
                    style={{ padding:'9px 12px', border:`1px solid ${P.s200}`, borderRadius:8,
                      fontSize:13, color:P.s700, backgroundColor:'#fff', cursor:'pointer' }}>
                    <option value="">{label}: Zote</option>
                    {opts.map(([v,l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                ))}

                <button onClick={() => setFilters({ search:'', service_type:'', status:'', chain_status:'', priority:'' })}
                  style={{ padding:'9px 14px', border:`1px solid ${P.s200}`, borderRadius:8,
                    backgroundColor:P.s50, color:P.s700, cursor:'pointer', fontSize:13, fontWeight:600 }}>
                  Futa Vichujio
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ── Results count ── */}
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
          <span style={{ fontSize:13, color:P.s500, fontWeight:600 }}>
            Maombi {filtered.length} / {requests.length}
          </span>
          {selectedIds.length > 0 && (
            <span style={{ fontSize:13, color:P.teal, fontWeight:700 }}>
              {selectedIds.length} yamechaguliwa
            </span>
          )}
        </div>

        {/* ── Request Cards ── */}
        {loading ? (
          <div style={{ textAlign:'center', padding:'60px 0', color:P.s400 }}>
            <RefreshCw size={32} style={{ animation:'spin 1s linear infinite', margin:'0 auto 12px' }} />
            <div style={{ fontSize:14, fontWeight:600 }}>Inapakia maombi...</div>
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign:'center', padding:'60px 0', color:P.s400 }}>
            <FileText size={48} style={{ margin:'0 auto 12px', display:'block' }} />
            <div style={{ fontSize:15, fontWeight:600, color:P.s600 }}>Hakuna maombi yanayolingana</div>
          </div>
        ) : (
          <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
            {filtered.map(req => {
              const sc  = STATUS_CFG[req.status]            || { label: req.status,            color: P.s400, bg: P.s100 };
              const cc  = CHAIN_STATUS_CFG[req.approval_chain_status] || { label: req.approval_chain_status, color: P.s400, bg: P.s100 };
              const exp = expanded === req.id;
              const reqChainSteps  = chains[req.id] || [];
              // Standard case: all officer steps done, waiting for admin
              // Edge case: gang=null request — only 1 admin step in chain
              const isAdminOnlyChain = reqChainSteps.length === 1 &&
                reqChainSteps[0]?.approver_type === 'admin' &&
                reqChainSteps[0]?.decision === 'pending';
              // The server decides whether the admin may act: officers go first when every
              // position in the gang's chain is filled; otherwise the admin can act directly.
              const adminCanDecide = req.admin_can_decide !== undefined
                ? req.admin_can_decide
                : (req.approval_chain_status === 'pending_admin' || isAdminOnlyChain);
              const isPendingAdmin = adminCanDecide;
              const waitingForOfficers = !adminCanDecide
                && !['admin_approved', 'admin_rejected'].includes(req.approval_chain_status);
              const isInProgress   = req.approval_chain_status === 'in_progress';
              const reqChain       = reqChainSteps;  // alias used throughout card

              return (
                <div key={req.id} style={{
                  backgroundColor: '#fff',
                  border: `1.5px solid ${isPendingAdmin ? '#fde68a' : isInProgress ? P.s200 : P.s200}`,
                  borderRadius: 14,
                  overflow: 'hidden',
                  boxShadow: isPendingAdmin ? '0 0 0 3px rgba(245,158,11,0.12)' : 'none',
                }}>
                  {/* Card header — always visible */}
                  <div style={{ padding:'16px 20px' }}>
                    <div style={{ display:'flex', alignItems:'flex-start', gap:12, flexWrap:'wrap' }}>

                      {/* Ref + status chips */}
                      <div style={{ flex:1 }}>
                        <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginBottom:8, alignItems:'center' }}>
                          <span style={{ fontFamily:'monospace', fontSize:11, backgroundColor:P.s100,
                            color:P.s700, borderRadius:6, padding:'2px 8px' }}>
                            {req.reference_number}
                          </span>
                          <Chip {...sc} />
                          {req.approval_chain_status && req.approval_chain_status !== 'not_started' && (
                            <Chip {...cc} />
                          )}
                          {isPendingAdmin && (
                            <span style={{ fontSize:10, color:P.amber, fontWeight:700,
                              backgroundColor:'#fef3c7', borderRadius:20, padding:'2px 9px',
                              animation:'pulse 1.5s infinite' }}>
                              ⚡ Inahitaji Idhini Yako
                            </span>
                          )}
                        </div>

                        <div style={{ fontSize:15, fontWeight:700, color:P.s800, marginBottom:6 }}>
                          {req.title}
                        </div>

                        {/* Meta row */}
                        <div style={{ display:'flex', flexWrap:'wrap', gap:16, fontSize:12, color:P.s500 }}>
                          <span>👤 {req.user?.name || '—'}</span>
                          <span>📅 {fmtDate(req.created_at || req.request_date)}</span>
                          {req.requested_amount > 0 && (
                            <span style={{ color:P.teal, fontWeight:700 }}>
                              💰 TZS {fmt(req.requested_amount)}
                            </span>
                          )}
                          {req.gang_details?.name && (
                            <span>🏛️ {req.gang_details.name}</span>
                          )}
                        </div>
                      </div>

                      {/* Mini chain dots */}
                      {reqChain.length > 0 && (
                        <div style={{ display:'flex', alignItems:'center', gap:4 }}>
                          {reqChain.map((step, i) => {
                            const bg = step.decision === 'approved' ? P.emerald
                                     : step.decision === 'rejected'  ? P.rose
                                     : step.step_number === req.current_approval_step ? P.amber
                                     : P.s200;
                            return (
                              <React.Fragment key={i}>
                                <div style={{ width:20, height:20, borderRadius:10,
                                  backgroundColor:bg, display:'flex', alignItems:'center',
                                  justifyContent:'center', fontSize:9, color:'#fff', fontWeight:700 }}>
                                  {step.decision === 'approved' ? '✓' : step.decision === 'rejected' ? '✕' : step.step_number}
                                </div>
                                {i < reqChain.length - 1 && (
                                  <div style={{ width:12, height:2,
                                    backgroundColor: step.decision === 'approved' ? P.emerald : P.s200 }} />
                                )}
                              </React.Fragment>
                            );
                          })}
                        </div>
                      )}

                      {/* Delete button */}
                      <button onClick={() => setDeleteModal({ id: req.id, title: req.title })}
                        title="Futa ombi"
                        style={{ padding:'8px 10px', borderRadius:9, border:`1px solid ${P.rose}22`,
                          backgroundColor:'#fff1f2', color:P.rose, cursor:'pointer', flexShrink:0,
                          display:'flex', alignItems:'center', justifyContent:'center' }}>
                        <Trash2 size={14} />
                      </button>

                      {/* Expand toggle */}
                      <button onClick={() => toggleExpand(req.id)}
                        style={{ padding:'8px 14px', borderRadius:9, border:`1px solid ${P.s200}`,
                          backgroundColor: exp ? P.navy : P.s50, color: exp ? '#fff' : P.s700,
                          cursor:'pointer', fontWeight:600, fontSize:12,
                          display:'flex', alignItems:'center', gap:5, flexShrink:0 }}>
                        {exp ? 'Funga' : 'Kagua'}
                        {exp ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                      </button>
                    </div>
                  </div>

                  {/* ── Expanded detail ── */}
                  {exp && (
                    <div style={{ borderTop:`1px solid ${P.s100}`, padding:'16px 20px 20px',
                      backgroundColor:P.s50 }}>

                      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20 }}>

                        {/* Left: chain */}
                        <div>
                          <ChainView steps={reqChain} currentStep={req.current_approval_step}
                            chainStatus={req.approval_chain_status} />
                        </div>

                        {/* Right: details + admin action */}
                        <div>
                          {/* Request details */}
                          <div style={{ backgroundColor:'#fff', borderRadius:12, padding:16,
                            border:`1px solid ${P.s200}`, marginBottom:12 }}>
                            <div style={{ fontSize:11, fontWeight:700, color:P.s500,
                              textTransform:'uppercase', letterSpacing:'.6px', marginBottom:10 }}>
                              Maelezo ya Ombi
                            </div>
                            {[
                              ['Mtumiaji', req.user?.name],
                              ['Barua pepe', req.user?.email],
                              ['Aina ya Huduma', req.service_type_display || req.service_type],
                              ['Kiasi Kilichoombwa', req.requested_amount > 0 ? `TZS ${fmt(req.requested_amount)}` : null],
                              ['Kiasi Kilichoidhinishwa', req.approved_amount > 0 ? `TZS ${fmt(req.approved_amount)}` : null],
                              ['Tarehe ya Ombi', fmtDate(req.request_date || req.created_at)],
                              ['Hatua ya Sasa', `${req.current_approval_step}/${reqChain.length}`],
                            ].filter(([, v]) => v).map(([l, v]) => (
                              <div key={l} style={{ display:'flex', justifyContent:'space-between',
                                marginBottom:6, fontSize:13 }}>
                                <span style={{ color:P.s500 }}>{l}</span>
                                <span style={{ fontWeight:600, color:P.s800 }}>{v}</span>
                              </div>
                            ))}
                            {req.description && (
                              <div style={{ marginTop:8, paddingTop:8, borderTop:`1px solid ${P.s100}` }}>
                                <div style={{ fontSize:11, color:P.s500, marginBottom:3 }}>Maelezo</div>
                                <div style={{ fontSize:12, color:P.s700, lineHeight:1.6 }}>{req.description}</div>
                              </div>
                            )}
                            {req.review_notes && (
                              <div style={{ marginTop:8, paddingTop:8, borderTop:`1px solid ${P.s100}` }}>
                                <div style={{ fontSize:11, color:P.s500, marginBottom:3 }}>Maelezo ya Mkaguzi</div>
                                <div style={{ fontSize:12, color:P.s700, fontStyle:'italic' }}>{req.review_notes}</div>
                              </div>
                            )}
                            {req.form_data && Object.keys(req.form_data).length > 0 && (
                              <div style={{ marginTop:8, paddingTop:8, borderTop:`1px solid ${P.s100}` }}>
                                <div style={{ fontSize:11, color:P.s500, marginBottom:6 }}>
                                  Taarifa za Ombi (Submitted Details)
                                </div>
                                {Object.entries(req.form_data).map(([key, value]) => (
                                  <div key={key} style={{ display:'flex', justifyContent:'space-between',
                                    gap:12, marginBottom:5, fontSize:12 }}>
                                    <span style={{ color:P.s500, flexShrink:0 }}>{humanizeKey(key)}</span>
                                    <span style={{ fontWeight:600, color:P.s800, textAlign:'right' }}>
                                      {formatFormDataValue(value)}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>

                          {/* Admin final approve — shown only when all officers done */}
                          {isPendingAdmin && (
                            <AdminApprovePanel
                              request={req}
                              onApprove={(payload) => handleAdminApprove(req.id, payload)}
                              loading={actionLoading}
                            />
                          )}

                          {/* Faint panel: the gang's positions are all filled, so its
                              officers approve before the admin can pay out. */}
                          {waitingForOfficers && (
                            <div style={{ backgroundColor:'#fff', borderRadius:12, padding:16,
                              border:`1px dashed ${P.s300}`, opacity:.65 }}>
                              <div style={{ fontSize:11, fontWeight:700, color:P.s500,
                                textTransform:'uppercase', letterSpacing:'.6px', marginBottom:6 }}>
                                Final approval locked
                              </div>
                              <p style={{ fontSize:12.5, color:P.s600, margin:0, lineHeight:1.5 }}>
                                This gang has someone in every approval position, so the request
                                follows the normal procedure. Final approval opens once the officers
                                have approved it.
                              </p>
                              <button disabled
                                style={{ marginTop:12, padding:'9px 16px', borderRadius:10,
                                  border:`1px solid ${P.s200}`, backgroundColor:P.s100, color:P.s400,
                                  fontWeight:700, fontSize:12.5, cursor:'not-allowed' }}>
                                Approve &amp; pay beneficiary
                              </button>
                            </div>
                          )}

                          {/* Quick status override for other states */}
                          {!isPendingAdmin && !['admin_approved','admin_rejected','rejected_by_officer'].includes(req.approval_chain_status) && (
                            <div style={{ backgroundColor:'#fff', borderRadius:12, padding:16,
                              border:`1px solid ${P.s200}` }}>
                              <div style={{ fontSize:11, fontWeight:700, color:P.s500,
                                textTransform:'uppercase', letterSpacing:'.6px', marginBottom:10 }}>
                                Badilisha Hali (Override)
                              </div>
                              <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                                {['approved','rejected','under_review','completed'].map(s => {
                                  const c = STATUS_CFG[s];
                                  return (
                                    <button key={s} onClick={() => handleQuickStatus(req.id, { status: s })}
                                      disabled={actionLoading || req.status === s}
                                      style={{ padding:'7px 14px', borderRadius:8, border:`1px solid ${c.color}30`,
                                        backgroundColor: req.status === s ? c.bg : '#fff',
                                        color: c.color, fontWeight:600, fontSize:12, cursor:'pointer',
                                        opacity: actionLoading ? .6 : 1 }}>
                                      {c.label}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── REPORT PANEL ── */}
      {showReport && (
        <div style={{
          position:'fixed', inset:0, backgroundColor:'rgba(0,0,0,0.55)',
          zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:20
        }}>
          <div style={{ backgroundColor:'#fff', borderRadius:18, width:'100%', maxWidth:820,
            maxHeight:'90vh', overflow:'auto', boxShadow:'0 24px 80px rgba(0,0,0,0.25)' }}>

            {/* Modal header */}
            <div style={{ padding:'22px 28px', borderBottom:`1px solid ${P.s100}`,
              display:'flex', alignItems:'center', justifyContent:'space-between',
              background:`linear-gradient(135deg, ${P.navy}, #002d5a)`, borderRadius:'18px 18px 0 0' }}>
              <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                <BarChart2 size={22} color='#5eead4' />
                <div>
                  <div style={{ color:'#fff', fontWeight:800, fontSize:18 }}>Taarifa ya Maombi</div>
                  <div style={{ color:'rgba(255,255,255,0.5)', fontSize:12 }}>
                    Muhtasari wa maombi {requests.length} yote
                  </div>
                </div>
              </div>
              <button onClick={() => setShowReport(false)}
                style={{ background:'rgba(255,255,255,0.1)', border:'none', borderRadius:8,
                  width:36, height:36, color:'#fff', cursor:'pointer', fontSize:20,
                  display:'flex', alignItems:'center', justifyContent:'center' }}>×</button>
            </div>

            <div style={{ padding:28 }}>

              {/* Key metrics row */}
              <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12, marginBottom:24 }}>
                {[
                  { label:'Maombi Yote',      val: requests.length,                icon:<FileText size={18}/>,  color:P.navy,    bg:'#dbeafe' },
                  { label:'Waombaji',          val: reportData.uniqueUsers,          icon:<Users size={18}/>,     color:P.violet,  bg:'#ede9fe' },
                  { label:'Jumla Iliyoombwa',  val:`TZS ${fmt(reportData.totalAmount)}`, icon:<DollarSign size={18}/>, color:P.teal, bg:'#ccfbf1' },
                  { label:'Wastani (Siku)',     val: reportData.avgDays,              icon:<Clock size={18}/>,     color:P.amber,   bg:'#fef3c7' },
                ].map(({ label, val, icon, color, bg }) => (
                  <div key={label} style={{ backgroundColor:bg, borderRadius:12, padding:'14px 16px' }}>
                    <div style={{ color, marginBottom:6 }}>{icon}</div>
                    <div style={{ fontSize:20, fontWeight:800, color }}>{val}</div>
                    <div style={{ fontSize:11, color, opacity:.7, fontWeight:600 }}>{label}</div>
                  </div>
                ))}
              </div>

              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20 }}>

                {/* By Status */}
                <div style={{ backgroundColor:P.s50, borderRadius:12, padding:18 }}>
                  <div style={{ fontWeight:700, fontSize:13, color:P.s800, marginBottom:14,
                    display:'flex', alignItems:'center', gap:7 }}>
                    <Activity size={15} color={P.teal} /> Hali za Maombi
                  </div>
                  {reportData.byStatus.map(({ label, count, color, bg }) => (
                    <div key={label} style={{ marginBottom:10 }}>
                      <div style={{ display:'flex', justifyContent:'space-between', marginBottom:4 }}>
                        <span style={{ fontSize:12, color:P.s700, fontWeight:600 }}>{label}</span>
                        <span style={{ fontSize:12, fontWeight:800, color }}>{count}</span>
                      </div>
                      <div style={{ height:6, backgroundColor:P.s200, borderRadius:3, overflow:'hidden' }}>
                        <div style={{
                          height:'100%', borderRadius:3, backgroundColor:color,
                          width: requests.length ? `${Math.round((count/requests.length)*100)}%` : '0%',
                          transition:'width .6s ease'
                        }} />
                      </div>
                    </div>
                  ))}
                </div>

                {/* By Chain Status */}
                <div style={{ backgroundColor:P.s50, borderRadius:12, padding:18 }}>
                  <div style={{ fontWeight:700, fontSize:13, color:P.s800, marginBottom:14,
                    display:'flex', alignItems:'center', gap:7 }}>
                    <Layers size={15} color={P.blue} /> Hali ya Mnyororo
                  </div>
                  {reportData.byChain.filter(x => x.count > 0).map(({ label, count, color, bg }) => (
                    <div key={label} style={{ marginBottom:10 }}>
                      <div style={{ display:'flex', justifyContent:'space-between', marginBottom:4 }}>
                        <span style={{ fontSize:12, color:P.s700, fontWeight:600 }}>{label}</span>
                        <span style={{ fontSize:12, fontWeight:800, color }}>{count}</span>
                      </div>
                      <div style={{ height:6, backgroundColor:P.s200, borderRadius:3, overflow:'hidden' }}>
                        <div style={{
                          height:'100%', borderRadius:3, backgroundColor:color,
                          width: requests.length ? `${Math.round((count/requests.length)*100)}%` : '0%',
                        }} />
                      </div>
                    </div>
                  ))}
                </div>

                {/* By Service Type */}
                <div style={{ backgroundColor:P.s50, borderRadius:12, padding:18 }}>
                  <div style={{ fontWeight:700, fontSize:13, color:P.s800, marginBottom:14,
                    display:'flex', alignItems:'center', gap:7 }}>
                    <PieChart size={15} color={P.violet} /> Aina ya Huduma
                  </div>
                  {reportData.byServiceType.map(({ label, count }) => (
                    <div key={label} style={{ display:'flex', justifyContent:'space-between',
                      alignItems:'center', padding:'7px 0', borderBottom:`1px solid ${P.s200}` }}>
                      <span style={{ fontSize:12, color:P.s700, fontWeight:600, textTransform:'capitalize' }}>
                        {label.replace(/_/g,' ')}
                      </span>
                      <span style={{ fontSize:13, fontWeight:800, color:P.navy,
                        backgroundColor:P.s100, borderRadius:20, padding:'2px 10px' }}>{count}</span>
                    </div>
                  ))}
                </div>

                {/* Financial summary */}
                <div style={{ backgroundColor:P.s50, borderRadius:12, padding:18 }}>
                  <div style={{ fontWeight:700, fontSize:13, color:P.s800, marginBottom:14,
                    display:'flex', alignItems:'center', gap:7 }}>
                    <DollarSign size={15} color={P.emerald} /> Muhtasari wa Fedha
                  </div>
                  {[
                    ['Jumla Iliyoombwa',     `TZS ${fmt(reportData.totalAmount)}`,   P.navy],
                    ['Jumla Iliyoidhinishwa',`TZS ${fmt(reportData.approvedAmount)}`,P.emerald],
                    ['Asilimia Iliyoidhini', requests.length
                      ? `${Math.round((requests.filter(r=>r.status==='approved').length/requests.length)*100)}%`
                      : '0%',                                                         P.teal],
                  ].map(([l,v,c]) => (
                    <div key={l} style={{ display:'flex', justifyContent:'space-between',
                      padding:'9px 0', borderBottom:`1px solid ${P.s200}` }}>
                      <span style={{ fontSize:12, color:P.s500 }}>{l}</span>
                      <span style={{ fontSize:13, fontWeight:800, color:c }}>{v}</span>
                    </div>
                  ))}

                  {/* Recent 7-day activity */}
                  <div style={{ marginTop:14 }}>
                    <div style={{ fontSize:11, color:P.s400, fontWeight:700,
                      textTransform:'uppercase', letterSpacing:'.5px', marginBottom:8 }}>
                      Siku 7 Zilizopita
                    </div>
                    <div style={{ display:'flex', gap:4, alignItems:'flex-end', height:50 }}>
                      {Array.from({length:7}).map((_,i) => {
                        const d = new Date(); d.setDate(d.getDate() - (6 - i));
                        const ds = d.toISOString().split('T')[0];
                        const cnt = requests.filter(r => (r.created_at||r.request_date||'').startsWith(ds)).length;
                        const maxCnt = Math.max(1, ...Array.from({length:7}).map((_,j) => {
                          const dd = new Date(); dd.setDate(dd.getDate()-(6-j));
                          return requests.filter(r => (r.created_at||'').startsWith(dd.toISOString().split('T')[0])).length;
                        }));
                        return (
                          <div key={i} style={{ flex:1, display:'flex', flexDirection:'column',
                            alignItems:'center', gap:3 }}>
                            <div title={`${ds}: ${cnt}`} style={{
                              width:'100%', backgroundColor: cnt>0 ? P.teal : P.s200,
                              borderRadius:3, height: `${Math.max(8, Math.round((cnt/maxCnt)*40))}px`,
                              transition:'height .4s ease'
                            }} />
                            <div style={{ fontSize:9, color:P.s400 }}>
                              {d.toLocaleDateString('sw-TZ',{weekday:'narrow'})}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>

              {/* Export button */}
              <div style={{ marginTop:20, display:'flex', justifyContent:'flex-end', gap:10 }}>
                <button onClick={doExport}
                  style={{ padding:'10px 20px', borderRadius:10, border:'none',
                    backgroundColor:P.teal, color:'#fff', cursor:'pointer',
                    fontWeight:700, fontSize:13, display:'flex', alignItems:'center', gap:7 }}>
                  <Download size={15} /> Pakua CSV
                </button>
                <button onClick={() => setShowReport(false)}
                  style={{ padding:'10px 20px', borderRadius:10, border:`1px solid ${P.s200}`,
                    backgroundColor:'#fff', color:P.s700, cursor:'pointer', fontWeight:600, fontSize:13 }}>
                  Funga
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── DELETE CONFIRM MODAL ── */}
      {deleteModal && (
        <div style={{
          position:'fixed', inset:0, backgroundColor:'rgba(0,0,0,0.55)',
          zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:20
        }}>
          <div style={{ backgroundColor:'#fff', borderRadius:16, width:'100%', maxWidth:420,
            padding:28, boxShadow:'0 24px 80px rgba(0,0,0,0.25)' }}>

            <div style={{ display:'flex', alignItems:'center', gap:14, marginBottom:18 }}>
              <div style={{ width:48, height:48, borderRadius:12, backgroundColor:'#ffe4e6',
                display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                <AlertOctagon size={24} color={P.rose} />
              </div>
              <div>
                <div style={{ fontWeight:800, fontSize:16, color:P.s800 }}>Futa Ombi</div>
                <div style={{ fontSize:12, color:P.s500, marginTop:2 }}>Hatua hii haiwezi kutenduliwa</div>
              </div>
            </div>

            <div style={{ backgroundColor:'#fff1f2', borderRadius:10, padding:'12px 16px',
              marginBottom:20, fontSize:13, color:'#9f1239' }}>
              Unakusudia kufuta ombi hili:<br/>
              <strong style={{ display:'block', marginTop:4 }}>"{deleteModal.title}"</strong>
            </div>

            <div style={{ display:'flex', gap:10 }}>
              <button onClick={() => setDeleteModal(null)} disabled={deleting}
                style={{ flex:1, padding:'11px 0', borderRadius:10, border:`1px solid ${P.s200}`,
                  backgroundColor:'#fff', color:P.s700, cursor:'pointer', fontWeight:600, fontSize:13 }}>
                Ghairi
              </button>
              <button onClick={handleDelete} disabled={deleting}
                style={{ flex:1, padding:'11px 0', borderRadius:10, border:'none',
                  backgroundColor:P.rose, color:'#fff', cursor:'pointer', fontWeight:700,
                  fontSize:13, display:'flex', alignItems:'center', justifyContent:'center', gap:7,
                  opacity: deleting ? .7 : 1 }}>
                <Trash2 size={14} />
                {deleting ? 'Inafuta...' : 'Ndio, Futa'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes pulse { 0%,100% { opacity:1; } 50% { opacity:.5; } }
        @keyframes slideIn { from { transform:translateX(20px); opacity:0; } to { transform:translateX(0); opacity:1; } }
      `}</style>
    </div>
  );
};

export default ServiceRequestManager;